import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsEmail,
  IsIn,
  IsInt,
  IsISO8601,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';

/**
 * Tickets — by-law offence notices — and the fines they carry.
 *
 * A ticket is written by an officer against the owner of a file's property,
 * for one offence from the municipality's fine schedule. The amount is copied
 * from the schedule at the moment of issue and never recomputed: a schedule
 * edited next month does not change what this owner was told they owe.
 *
 * Lifecycle:
 *
 *   issued ──serve──▶ served ──(due date passes)──▶ overdue ──▶ collections
 *     │                 │  ▲                          │
 *     │                 ▼  │ dispute rejected         │
 *     │              disputed ─── dispute upheld ──▶ void
 *     └───────────────────── void ◀───────────────────┘
 *
 * and `paid` from anywhere a balance is still owed. `paid` and `void` are
 * terminal. Every move is audited on the file's trail.
 */

export const TICKET_STATUSES = [
  'issued',
  'served',
  'paid',
  'disputed',
  'overdue',
  'collections',
  'void',
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

/** Which statuses each status may move to. Terminal statuses move nowhere. */
export const TICKET_NEXT: Record<TicketStatus, readonly TicketStatus[]> = {
  issued: ['served', 'paid', 'void'],
  served: ['paid', 'disputed', 'overdue', 'void'],
  overdue: ['paid', 'disputed', 'collections', 'void'],
  // A rejected dispute puts the ticket back to `served` with a fresh due date;
  // an upheld one voids it. Paying while disputed withdraws the dispute.
  disputed: ['served', 'paid', 'void'],
  // Collections can still be settled with the municipality directly.
  collections: ['paid'],
  paid: [],
  void: [],
};

export function canTransitionTicket(from: TicketStatus, to: TicketStatus): boolean {
  return TICKET_NEXT[from].includes(to);
}

/** A balance is owed, so the owner (or the counter) may pay it. */
export const PAYABLE_TICKET_STATUSES: readonly TicketStatus[] = [
  'issued',
  'served',
  'overdue',
  'disputed',
  'collections',
];

export function isTicketPayable(status: TicketStatus): boolean {
  return PAYABLE_TICKET_STATUSES.includes(status);
}

/** Counted as outstanding on the dashboard: owed, and not written off. */
export const OUTSTANDING_TICKET_STATUSES: readonly TicketStatus[] = PAYABLE_TICKET_STATUSES;

/** How the notice reached the owner. */
export const TICKET_SERVICE_METHODS = [
  'personal',
  'registered_mail',
  'posted_on_property',
  'email',
] as const;
export type TicketServiceMethod = (typeof TICKET_SERVICE_METHODS)[number];

/** How a payment taken at the counter was made. Online payments are `card_online`. */
export const COUNTER_PAYMENT_METHODS = ['cash', 'cheque', 'card_in_person', 'other'] as const;
export type CounterPaymentMethod = (typeof COUNTER_PAYMENT_METHODS)[number];
export type TicketPaymentMethod = CounterPaymentMethod | 'card_online';

export const TICKET_PAYMENT_STATUSES = ['pending', 'succeeded', 'failed', 'expired'] as const;
export type TicketPaymentStatus = (typeof TICKET_PAYMENT_STATUSES)[number];

export const TICKET_CONFIG = {
  /** Days to pay after service, when the schedule entry does not say. */
  defaultPaymentDays: 30,
  /** A dispute rejected gives the owner this long again to pay. */
  disputeRejectedPaymentDays: 15,
  /** The "due soon" reminder goes out this many days before the due date. */
  dueSoonReminderDays: 7,
  /** Days overdue before a ticket may be sent to collections. */
  collectionsAfterOverdueDays: 30,
  /** An offline-issued ticket may carry an issue time at most this old. */
  maxOfflineAgeHours: 72,
  /** Largest fine a schedule entry may set, in cents ($100,000). */
  maxAmountCents: 10_000_000,
  currency: 'CAD',
} as const;

/** The due date for a ticket served at `servedAt`, `days` days to pay. */
export function ticketDueAt(servedAt: Date, days: number): Date {
  return new Date(servedAt.getTime() + days * 24 * 60 * 60 * 1000);
}

/**
 * Which reminder, if any, a ticket is due for at `now`: `due_soon` inside the
 * last week before the due date, `overdue` once it has passed. Null for a
 * ticket nothing is owed on, or one that has not been served (no due date).
 */
export function ticketReminderStage(
  ticket: { status: TicketStatus; dueAt: string | Date | null },
  now: Date,
): 'due_soon' | 'overdue' | null {
  if (!ticket.dueAt) return null;
  if (ticket.status !== 'served' && ticket.status !== 'overdue') return null;
  const due = new Date(ticket.dueAt).getTime();
  if (now.getTime() >= due) return 'overdue';
  const soon = due - TICKET_CONFIG.dueSoonReminderDays * 24 * 60 * 60 * 1000;
  return now.getTime() >= soon ? 'due_soon' : null;
}

/** May an overdue ticket go to collections yet? */
export function collectionsAllowed(dueAt: string | Date | null, now: Date): boolean {
  if (!dueAt) return false;
  const threshold =
    new Date(dueAt).getTime() + TICKET_CONFIG.collectionsAfterOverdueDays * 24 * 60 * 60 * 1000;
  return now.getTime() >= threshold;
}

/** `T-2026-000123` — the printed ticket number, from a database sequence. */
export function formatTicketReference(year: number, sequence: number): string {
  return `T-${year}-${String(sequence).padStart(6, '0')}`;
}

/** `R-2026-000007` — the printed receipt number, from a database sequence. */
export function formatReceiptNumber(year: number, sequence: number): string {
  return `R-${year}-${String(sequence).padStart(6, '0')}`;
}

/**
 * What a confirmed payment does to a ticket: the paid total goes up, and the
 * ticket is `paid` once nothing is owed. A partial payment leaves the status
 * alone — an overdue ticket half paid is still overdue.
 *
 * Shared by the counter (gov-api) and the online webhook (public-api) so the
 * two can never disagree about when a fine is settled.
 */
export function settleTicket(
  ticket: { status: TicketStatus; amountCents: number; amountPaidCents: number },
  paymentCents: number,
): { status: TicketStatus; amountPaidCents: number; balanceCents: number; settled: boolean } {
  if (!Number.isInteger(paymentCents) || paymentCents <= 0) {
    throw new RangeError(`paymentCents must be a positive integer, got ${paymentCents}`);
  }
  const amountPaidCents = ticket.amountPaidCents + paymentCents;
  const balanceCents = Math.max(0, ticket.amountCents - amountPaidCents);
  const settled = balanceCents === 0;
  return {
    status: settled ? 'paid' : ticket.status,
    amountPaidCents,
    balanceCents,
    settled,
  };
}

export function ticketBalanceCents(ticket: { amountCents: number; amountPaidCents: number }) {
  return Math.max(0, ticket.amountCents - ticket.amountPaidCents);
}

// ── Fine schedule ──────────────────────────────────────────────────────────

export interface FineScheduleEntry {
  id: string;
  localGovernmentId: string;
  /** Short code printed on the ticket, e.g. "DU-01". Unique per municipality. */
  code: string;
  offenceEn: string;
  offenceFr: string;
  /** The by-law or statute, when the offence is tied to a registry entry. */
  enforcedLawId: string | null;
  /** The section, as printed: "s. 12(1)". */
  bylawSection: string;
  amountCents: number;
  /** Days to pay after service. */
  paymentDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;

export class CreateFineScheduleEntryDto {
  @Matches(CODE_PATTERN, { message: 'code must be letters, digits, dot, dash or underscore' })
  code!: string;

  @IsString()
  @MinLength(3)
  @MaxLength(400)
  offenceEn!: string;

  @IsString()
  @MinLength(3)
  @MaxLength(400)
  offenceFr!: string;

  @IsOptional()
  @IsUUID()
  enforcedLawId?: string | null;

  @IsString()
  @MaxLength(120)
  bylawSection!: string;

  @IsInt()
  @Min(1)
  @Max(TICKET_CONFIG.maxAmountCents)
  amountCents!: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(365)
  paymentDays?: number;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;
}

export class UpdateFineScheduleEntryDto {
  @IsOptional()
  @IsString()
  @MinLength(3)
  @MaxLength(400)
  offenceEn?: string;

  @IsOptional()
  @IsString()
  @MinLength(3)
  @MaxLength(400)
  offenceFr?: string;

  @IsOptional()
  @IsUUID()
  enforcedLawId?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  bylawSection?: string;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(TICKET_CONFIG.maxAmountCents)
  amountCents?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(365)
  paymentDays?: number;

  @IsOptional()
  @IsBoolean()
  isActive?: boolean;
}

/** What the issue form needs for one file: is the module on, and what may be charged. */
export interface FileTicketOptions {
  localGovernmentId: string | null;
  /** The municipality's plan grants tickets. */
  enabled: boolean;
  /** Active schedule entries, by code. Empty when disabled. */
  schedule: FineScheduleEntry[];
  /** Defaults for the recipient, from the file's owner of record. */
  recipient: TicketRecipient;
}

// ── Tickets ────────────────────────────────────────────────────────────────

export interface TicketRecipient {
  name: string | null;
  email: string | null;
  mailingAddress: string | null;
  locale: 'en' | 'fr';
}

export interface TicketFix {
  lat: number;
  lng: number;
  accuracy: number | null;
}

export interface TicketPaymentView {
  id: string;
  method: TicketPaymentMethod;
  status: TicketPaymentStatus;
  amountCents: number;
  currency: string;
  /** Printed on the receipt. Null until the payment has succeeded. */
  receiptNumber: string | null;
  reference: string | null;
  confirmedAt: string | null;
  createdAt: string;
}

export interface TicketView {
  id: string;
  reference: string;
  fileId: string;
  fileReference: string;
  address: string;
  localGovernmentId: string;
  status: TicketStatus;
  offenceCode: string;
  offenceEn: string;
  offenceFr: string;
  bylawSection: string;
  amountCents: number;
  amountPaidCents: number;
  currency: string;
  recipient: TicketRecipient;
  issuedAt: string;
  issuedById: string | null;
  issuedByName: string | null;
  issuedLocation: TicketFix | null;
  evidenceIds: string[];
  notes: string | null;
  servedAt: string | null;
  serviceMethod: TicketServiceMethod | null;
  dueAt: string | null;
  paidAt: string | null;
  disputedAt: string | null;
  disputeReason: string | null;
  disputeOutcome: 'upheld' | 'rejected' | null;
  collectionsAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  payments: TicketPaymentView[];
  createdAt: string;
  updatedAt: string;
}

/** A ticket row in a municipality-wide list. */
export type TicketListItem = Omit<TicketView, 'payments' | 'evidenceIds' | 'notes'>;

class FixFields {
  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-90)
  @Max(90)
  lat?: number | null;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-180)
  @Max(180)
  lng?: number | null;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(0)
  @Max(100_000)
  accuracy?: number | null;
}

/** `POST /files/:fileId/tickets` — write a ticket. */
export class IssueTicketDto extends FixFields {
  @IsUUID()
  fineScheduleEntryId!: string;

  /**
   * Minted on the device when the officer taps Issue, and sent again on every
   * replay of that tap. The server keeps one ticket per key, so a phone that
   * lost the response and retries from its offline queue gets the same ticket
   * back rather than fining the owner twice.
   */
  @IsOptional()
  @IsUUID()
  clientRequestId?: string;

  /**
   * When the officer wrote the ticket, as the device saw it. For tickets
   * written offline and sent later; bounded so a broken clock cannot date a
   * ticket in the future or months back. Defaults to the time of receipt.
   */
  @IsOptional()
  @IsISO8601()
  issuedAt?: string;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(20)
  @IsUUID('all', { each: true })
  evidenceIds?: string[];

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  notes?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  recipientName?: string;

  @IsOptional()
  @IsEmail()
  @MaxLength(320)
  recipientEmail?: string;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  recipientMailingAddress?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  recipientLocale?: 'en' | 'fr';

  /** Handed to the owner on the spot: issue and serve in one step. */
  @IsOptional()
  @IsIn(TICKET_SERVICE_METHODS)
  servedBy?: TicketServiceMethod;
}

/** `POST /tickets/:id/serve` */
export class ServeTicketDto {
  @IsIn(TICKET_SERVICE_METHODS)
  method!: TicketServiceMethod;

  /** When it was served, if not now (a registered-mail delivery date). */
  @IsOptional()
  @IsISO8601()
  servedAt?: string;
}

/** `POST /tickets/:id/dispute` — the owner contested it, recorded by staff. */
export class DisputeTicketDto {
  @IsString()
  @MinLength(3)
  @MaxLength(2000)
  reason!: string;
}

/** `POST /tickets/:id/dispute/decision` */
export class DecideTicketDisputeDto {
  @IsIn(['upheld', 'rejected'])
  outcome!: 'upheld' | 'rejected';

  @IsString()
  @MinLength(3)
  @MaxLength(2000)
  note!: string;
}

/** `POST /tickets/:id/void` */
export class VoidTicketDto {
  @IsString()
  @MinLength(3)
  @MaxLength(2000)
  reason!: string;
}

/** `POST /tickets/:id/collections` */
export class SendTicketToCollectionsDto {
  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}

/** `POST /tickets/:id/payments` — a payment taken at the counter. */
export class RecordTicketPaymentDto {
  @IsIn(COUNTER_PAYMENT_METHODS)
  method!: CounterPaymentMethod;

  @IsInt()
  @Min(1)
  @Max(TICKET_CONFIG.maxAmountCents)
  amountCents!: number;

  /** Cheque number, till receipt — whatever the counter writes down. */
  @IsOptional()
  @IsString()
  @MaxLength(120)
  reference?: string;

  /** Same role as on issue: one payment per key, however often it is sent. */
  @IsOptional()
  @IsUUID()
  clientRequestId?: string;
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export interface TicketMonthTotal {
  /** `YYYY-MM`, in Atlantic time. */
  month: string;
  collectedCents: number;
}

/** One municipality's fines, for the dashboard. */
export interface TicketLgSummary {
  localGovernmentId: string;
  name: string;
  enabled: boolean;
  outstandingCount: number;
  outstandingCents: number;
  overdueCount: number;
  overdueCents: number;
  disputedCount: number;
  collectionsCount: number;
  collectionsCents: number;
  /** Everything ever collected, online and at the counter. */
  collectedCents: number;
  /** Collected in the current calendar month. */
  collectedThisMonthCents: number;
  /** The last twelve months, oldest first. */
  byMonth: TicketMonthTotal[];
}

/** `GET /tickets/availability` — municipalities in reach with tickets turned on. */
export interface TicketAvailability {
  localGovernmentIds: string[];
}

// ── Client Portal ──────────────────────────────────────────────────────────

/**
 * A ticket as its addressee sees it. No officer name, no GPS, no evidence and
 * no internal notes — the served notice is what the owner has, and this is
 * that notice plus where its payment stands.
 */
export interface CitizenTicketView {
  id: string;
  reference: string;
  fileId: string;
  fileReference: string;
  address: string;
  status: TicketStatus;
  offenceCode: string;
  offenceEn: string;
  offenceFr: string;
  bylawSection: string;
  amountCents: number;
  amountPaidCents: number;
  balanceCents: number;
  currency: string;
  issuedAt: string;
  servedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  /** Online payment can be started now. */
  payable: boolean;
  receipts: { paymentId: string; receiptNumber: string; amountCents: number; paidAt: string }[];
}

/** `POST /portal/tickets/:id/pay` → where to send the owner. */
export interface StartTicketPaymentResult {
  /** The hosted payment page, or the portal's own confirmation page for the stub. */
  url: string;
  paymentId: string;
}
